import { Component, HostBinding, ViewEncapsulation, OnDestroy } from '@angular/core';
import {
	Observable,
	Subscription,
	map,
	debounceTime,
	distinctUntilChanged,
	startWith,
	of,
	switchMap,
	shareReplay,
	tap,
	defer,
	finalize,
} from 'rxjs';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FieldExpression, NodeExpression,
	phrase, toString as luceneToString, OperatorExpression, term } from 'lucene';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgFor } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { WEIGHT_OPTIONS } from '../../../shared/models/list';
import { User } from '../../../shared/models/user';
import { UserService } from '../../../shared/services/user.service';
import { OrderBy, OrderByDirection, UNDERSCORE } from '../../../shared/utils/constant';
import { GROSS } from '../../../shared/models/settings';
import { LocationService } from '../../../shared/services/location.service';
import { QualityIssueBatchService } from '../../services/quality-issue-batch-service';
import { CustomLocation } from '../../../shared/services/location.custom';
import { AllQualityIssue } from '../../models/all-quality-issue';
import { ClaimBatch } from '../../models/claim-batch';
import { StepStatus, StepperComponent } from '../../../shared/components/stepper/stepper.component';
import { TranslationService } from '../../../shared/services/translation.service';
import { AllQualityIssueService } from '../../services/all-quality-issue.service';
import { SoldToRelationshipGroup } from '../../../shared/models/service';
import { DialogBatchNotInvoicedComponent } from '../utils/dialog-batch-not-invoiced.component';
import { DimensionPipe } from '../../../shared/pipes/format-dimension.pipe';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';
import { SlideButtonComponent } from '../../../shared/components/slide-button/slide-button.component';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';

@Component({
	selector: 'copo-batch-selection',
	templateUrl: './batch-selection.component.html',
	styleUrl: './batch-selection.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		CdkScrollable,
		PageHeaderComponent,
		SlideButtonComponent,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatButtonModule,
		MatIconModule,
		MatRippleModule,
		TranslateModule,
		StepperComponent,
		VirtualScrollerDirective,

		MatRadioModule,
		RouterLink,
		NgFor,
		LoadingSpinnerComponent,
		NumberPipe,
		DimensionPipe
	]
})
export class BatchSelectionComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly block = true;

	user: User;

	viewDeclareMenu = false;

	requiredFields: (keyof ClaimBatch)[] = [
		'id',
		'customerOrderNumber',
		'steelgradeDescription',
		'qtyBatchNet',
		'qtyBatchGross',
		'supplierOrderNumber',
		'supplierOrderItemNumber',
		'cddWeek',
		'batchReference',
		'statusDescription',
		'millDescription',
		'millOrderNumber',
		'millOrderItemNumber',
		'soldTo',
		'soldToDescription',
		'shipTo',
		'qtyInvoiced',
		'weightUnitInvoiced',
		'currencyInvoiced',
		'sfcC',
		'thicknessDimension',
		'widthDimension',
		'lengthDimension',
		'lastDispatchAdviceNumber',
		'batchInvoiced',
		'invoiceDate'
	];

	searchTextControl: FormControl<string>;
	searchTextSubscription: Subscription;
	batchNotInvoicedSubscription?: Subscription;

	weightDisplay: keyof typeof WEIGHT_OPTIONS = GROSS;
	searchValue: string;
	searchValueChanging = false;
	togglePos = false;

	readonly UNDERSCORE = UNDERSCORE;
	mainClaimBatch: ClaimBatch | undefined;
	mainClaimBatchLoading = false;
	selectedBatch: ClaimBatch | undefined;

	readonly steps;

	soldToRelationshipGrps: Observable<SoldToRelationshipGroup[]>;

	private readonly batchDataSub: Subscription | undefined;
	private readonly claim: AllQualityIssue | undefined;
	private readonly stateId: number;

	constructor(
		private readonly userService: UserService,
		private readonly claimService: AllQualityIssueService,
		private readonly batchService: QualityIssueBatchService,
		private readonly locationService: LocationService,
		private readonly translationService: TranslationService,

		private readonly dialog: MatDialog,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
	) {
		this.steps = [
			{
				label: this.translationService.getTR('claim.first.batch.selection'),
				status: StepStatus.CURRENT,
			},
			{
				label: this.translationService.getTR('claim.quality.issue.information'),
				status: StepStatus.DISABLED,
			},
			{
				label: this.translationService.getTR('claim.quality.issue.summary'),
				status: StepStatus.DISABLED,
			}
		];
		this.stateId = CustomLocation.GET_CURRENT_STATE_ID();

		const searchState = <{ searchValue: string } | undefined> CustomLocation.GET_STATE('searchState', this.stateId);
		this.searchValue = !searchState ? '' : searchState.searchValue;
		this.searchTextControl = new FormControl(this.searchValue, { nonNullable: true });

		this.claim = (<{ claim?: AllQualityIssue }> this.route.snapshot.data).claim;

		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			tap(() => {
				this.searchValueChanging = true;
			}),
			debounceTime(300),
			tap(() => {
				this.searchValueChanging = false;
			}),
			startWith(this.searchTextControl.value),
			distinctUntilChanged()
		).subscribe((value: string) => {
			this.searchValue = value;
		});

		this.user = this.userService.user;
		const { user } = this;

		if (user.hasService('QUAPP')) {
			const isReader = user.isClaimReader();
			const isDetector = user.isClaimDetector();
			const isValidator = user.isClaimValidator();

			this.viewDeclareMenu = isDetector || isValidator || !isReader;
		}

		this.soldToRelationshipGrps = this.userService.getSoldToRelationshipGroupsTransac('QUAPP')
			.pipe(
				shareReplay({ bufferSize: 1, refCount: false })
			);
		if (this.claim?.batches[0].reference) {
			const [{ reference, quantityValue }] = this.claim.batches;
			this.batchDataSub = defer(() => {
				this.mainClaimBatchLoading = true;
				return this.soldToRelationshipGrps
					.pipe(
						// search reference/quantity
						switchMap(transacGrps => {
							if (transacGrps.length === 0) {
								return of(undefined);
							}
							const soldTos = transacGrps.map(soldtoList => soldtoList.soldTo);
							const shipTos = Array.from(new Set(transacGrps.flatMap(v => v.shipTos)));
							if (soldTos.length === 0 && shipTos.length === 0) {
								return of(undefined);
							}
							return this.getBatchData(soldTos, shipTos, reference, quantityValue);
						}),
						// search reference if not found
						switchMap(batchDataSoldToTransacs => {
							if (!batchDataSoldToTransacs) {
								return of(undefined);
							}
							const { batchData, soldToTransacs, shipToTransacs } = batchDataSoldToTransacs;
							if (batchData.results.length === 0) {
								return this.getBatchData(soldToTransacs, shipToTransacs, reference);
							}
							return of(batchDataSoldToTransacs);
						}),
						finalize(() => {
							this.mainClaimBatchLoading = false;
						}),
					);
			})
				.subscribe({
					next: batchDataSoldToTransacs => {
						const batchData = batchDataSoldToTransacs ? batchDataSoldToTransacs.batchData : undefined;
						if (batchData && batchData.results.length > 0) {
							this.mainClaimBatch = batchData.results[0];
						} else if (this.claim?.batches[0].reference && batchDataSoldToTransacs) {
							this.searchTextControl.reset(this.claim.batches[0].reference);
						}
					},
					error: (err: unknown) => {
						console.error('error getting batch data', err);
					}
				});
		}
	}

	get loading() {
		return this.searchValueChanging || this.mainClaimBatchLoading;
	}

	get dataId() {
		let selectedBatch = '';
		if (this.claim?.batches[0].reference) {
			const [{ reference, quantityValue }] = this.claim.batches;
			selectedBatch = `${ reference }/${ quantityValue }`;
		}
		return `claim-batch-entry-${ this.searchValue } -${ selectedBatch }-${ this.togglePos }`;
	}

	getBatchData(soldToTransacs: string[], shipToTransacs: string[], ref: string, qty?: number) {
		let allNode: FieldExpression | NodeExpression = {
			field: 'batchReference',
			term: phrase.escape(ref),
			quoted: true
		};
		if (qty) {
			const quantity1Field: FieldExpression = {
				field: 'qtyBatchGross',
				term: String(qty),
				quoted: false
			};
			const quantity2Field: FieldExpression = {
				field: 'qtyBatchGross',
				term: String(qty / 1000),
				quoted: false
			};
			const quantity3Field: FieldExpression = {
				field: 'qtyBatchNet',
				term: String(qty),
				quoted: false
			};
			const quantity4Field: FieldExpression = {
				field: 'qtyBatchNet',
				term: String(qty / 1000),
				quoted: false
			};
			const quantity5Field: FieldExpression = {
				field: 'qtyInvoiced',
				term: String(qty),
				quoted: false
			};
			const quantity6Field: FieldExpression = {
				field: 'qtyInvoiced',
				term: String(qty / 1000),
				quoted: false
			};

			const qty1Node: NodeExpression = {
				left: quantity1Field,
				operator: 'OR',
				parenthesized: false,
				right: quantity2Field
			};

			const qty2Node: NodeExpression = {
				left: qty1Node,
				operator: 'OR',
				parenthesized: false,
				right: quantity3Field
			};

			const qty3Node: NodeExpression = {
				left: qty2Node,
				operator: 'OR',
				parenthesized: false,
				right: quantity4Field
			};

			const qty4Node: NodeExpression = {
				left: qty3Node,
				operator: 'OR',
				parenthesized: false,
				right: quantity5Field
			};

			const qty5Node: NodeExpression = {
				left: qty4Node,
				operator: 'OR',
				parenthesized: true,
				right: quantity6Field
			};

			allNode = {
				left: allNode,
				operator: 'AND',
				parenthesized: false,
				right: qty5Node
			};
		}

		const transacNode = this.claimService.createTransacSoldtoAndShiptoFilter(soldToTransacs, shipToTransacs);

		allNode = {
			left: allNode,
			operator: 'AND',
			parenthesized: false,
			right: transacNode
		};

		return this.batchService.getBatches(
			this.requiredFields,
			luceneToString(allNode),
			1,
			10,
			new OrderBy('cdd', OrderByDirection.DESC)
		)
			.pipe(
				map(batchData => <const>{ batchData, soldToTransacs, shipToTransacs })
			);
	}

	ngOnDestroy() {
		this.searchTextSubscription.unsubscribe();
		if (this.batchDataSub) {
			this.batchDataSub.unsubscribe();
		}
		CustomLocation.SET_STATE(
			'searchState',
			this.stateId,
			{
				searchValue: this.searchTextControl.value
			}
		);
	}

	selectBatch(batch: ClaimBatch, event: MatRadioChange) {
		this.selectedBatch = event.source.checked ? batch : undefined;
	}

	goBack() {
		this.locationService.go(-1);
	}

	createQuickSearchFilter(textSearch: string, soldToTransacs: string [], shipToTransacs: string[]) {
		let globalNode;
		if (textSearch !== '') {
			const escapedTextFilter = `*${ term.escape(textSearch) }*`;

			const supplierOrderReferenceField: FieldExpression = {
				field: 'supplierOrderNumber',
				term: escapedTextFilter,
				quoted: false
			};

			const customerOrderReferenceField: FieldExpression = {
				field: 'customerOrderNumber',
				term: escapedTextFilter,
				quoted: false
			};

			const customerArticleReferenceField: FieldExpression = {
				field: 'customerArticleReference',
				term: escapedTextFilter,
				quoted: false
			};

			const steelgradeDescriptionField: FieldExpression = {
				field: 'steelgradeDescription',
				term: escapedTextFilter,
				quoted: false
			};

			const batchReferenceDescriptionField: FieldExpression = {
				field: 'batchReference',
				term: escapedTextFilter,
				quoted: false
			};

			const lastDispatchAdviceNumberField: FieldExpression = {
				field: 'lastDispatchAdviceNumber',
				term: escapedTextFilter,
				quoted: false
			};

			const millOrderNumberField: FieldExpression = {
				field: 'millOrderNumber',
				term: escapedTextFilter,
				quoted: false
			};

			const heatNumberField: FieldExpression = {
				field: 'heatNumber',
				term: escapedTextFilter,
				quoted: false
			};

			const supplierCustomerNode = {
				left: supplierOrderReferenceField,
				operator: 'OR',
				right: customerOrderReferenceField,
				parenthesized: false
			};

			const articleSteelgradeNode = {
				left: customerArticleReferenceField,
				operator: 'OR',
				right: steelgradeDescriptionField,
				parenthesized: false
			};

			const intermediateNode1 = {
				left: supplierCustomerNode,
				operator: 'OR',
				right: articleSteelgradeNode,
				parenthesized: false
			};

			const batchReferenceLastDispatchNode = {
				left: batchReferenceDescriptionField,
				operator: 'OR',
				right: lastDispatchAdviceNumberField,
				parenthesized: false
			};

			const millOrderHeatNode = {
				left: millOrderNumberField,
				operator: 'OR',
				right: heatNumberField,
				parenthesized: false
			};

			const intermediateNode2 = {
				left: batchReferenceLastDispatchNode,
				operator: 'OR',
				right: millOrderHeatNode,
				parenthesized: false
			};

			globalNode = {
				left: intermediateNode1,
				operator: 'OR',
				right: intermediateNode2,
				parenthesized: true
			};
		}

		if (!this.togglePos) {
			const batchInvoicedField: FieldExpression = {
				field: 'batchInvoiced',
				term: 'X',
				quoted: false
			};

			globalNode = globalNode
				? {
					left: globalNode,
					operator: 'AND',
					right: batchInvoicedField,
					parenthesized: true
				}
				: batchInvoicedField;
		}

		if (this.claim?.batches[0].reference) {
			const [{ reference, quantityValue }] = this.claim.batches;
			const batchReferenceNode: FieldExpression = {
				field: 'batchReference',
				term: phrase.escape(reference),
				quoted: false
			};

			const quantityNode: FieldExpression = {
				field: 'qtyInvoiced',
				term: String(quantityValue),
				quoted: false
			};

			const notBatchIdentifierNode: OperatorExpression = {
				start: 'NOT',
				left: {
					left: batchReferenceNode,
					operator: 'AND',
					parenthesized: true,
					right: quantityNode
				}
			};

			globalNode = globalNode
				? {
					left: globalNode,
					operator: 'AND',
					right: notBatchIdentifierNode,
					parenthesized: true
				}
				: notBatchIdentifierNode;
		}

		const transacNode = this.claimService.createTransacSoldtoAndShiptoFilter(soldToTransacs, shipToTransacs);

		return luceneToString(globalNode ? {
			left: globalNode,
			operator: 'AND',
			right: transacNode,
			parenthesized: false
		} : transacNode);
	}

	getBatchQuantity(batch: ClaimBatch) {
		const weight = this.weightDisplay;
		return weight === 'net' ? batch.qtyBatchNet : batch.qtyBatchGross;
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<ClaimBatch[]>) => void) {
		provideData(
			this.soldToRelationshipGrps
				.pipe(
					switchMap(transacGrps => {
						if (transacGrps.length === 0) {
							return of([]);
						}
						const soldTos = transacGrps.map(soldtoList => soldtoList.soldTo);
						const shipTos = Array.from(new Set(transacGrps.flatMap(v => v.shipTos)));
						if (soldTos.length === 0 && shipTos.length === 0) {
							return of([]);
						}
						return this.batchService.getBatches(
							this.requiredFields,
							this.createQuickSearchFilter(this.searchValue, soldTos, shipTos),
							index,
							limit,
							new OrderBy('cddWeek', OrderByDirection.DESC)
						)
							.pipe(map(res => res.results));
					})
				)
		);
	}

	settogglePos($event: boolean) {
		this.togglePos = $event;
	}

	async setMainBatchOnClaim() {
		this.selectedBatch ||= this.mainClaimBatch;
		if (!this.selectedBatch) {
			return;
		}
		if (!this.claimService.batchInvoicedOrNot(this.selectedBatch.batchInvoiced)) {
			const batchReferenceArray: string[] = [];
			batchReferenceArray.push(this.selectedBatch.batchReference);
			this.dialog.open<DialogBatchNotInvoicedComponent>(
				DialogBatchNotInvoicedComponent,
				{ data: batchReferenceArray,
					panelClass: 'copo-claim-dialog',
					disableClose: true }
			);
			return;
		}
		let { claim } = this;
		const { selectedBatch: claimBatch } = this;
		if (!claim) {
			claim = this.claimService.claim = new AllQualityIssue();
		}
		this.claimService.fillClaimFromMainBatch(claim, claimBatch);
		await this.router.navigate(['../quality-issue-entry'], { relativeTo: this.route });
	}
}
